import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaCreditCard, FaWhatsapp, FaBitcoin, FaCheckCircle, FaUser, FaEnvelope, FaLock, FaPhone, FaVenusMars, FaIdCard } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Códigos de país populares para el selector
const COUNTRY_CODES = [
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+1', country: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦' },
  { code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻' },
  { code: '+504', country: 'Honduras', flag: '🇭🇳' },
  { code: '+505', country: 'Nicaragua', flag: '🇳🇮' },
  { code: '+591', country: 'Bolivia', flag: '🇧🇴' },
  { code: '+595', country: 'Paraguay', flag: '🇵🇾' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
];

// Lista de países para el selector de moneda de pago.
// Colombia paga en COP, todos los demás en USD.
const PAYMENT_COUNTRIES = [
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: 'MX', name: 'México', flag: '🇲🇽' },
  { code: 'ES', name: 'España', flag: '🇪🇸' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'PE', name: 'Perú', flag: '🇵🇪' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'PA', name: 'Panamá', flag: '🇵🇦' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾' },
  { code: 'DO', name: 'República Dominicana', flag: '🇩🇴' },
  { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷' },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺' },
  { code: 'CA', name: 'Canadá', flag: '🇨🇦' },
  { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
  { code: 'GB', name: 'Reino Unido', flag: '🇬🇧' },
  { code: 'FR', name: 'Francia', flag: '🇫🇷' },
  { code: 'DE', name: 'Alemania', flag: '🇩🇪' },
  { code: 'IT', name: 'Italia', flag: '🇮🇹' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'NL', name: 'Países Bajos', flag: '🇳🇱' },
  { code: 'BE', name: 'Bélgica', flag: '🇧🇪' },
  { code: 'CH', name: 'Suiza', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'SE', name: 'Suecia', flag: '🇸🇪' },
  { code: 'NO', name: 'Noruega', flag: '🇳🇴' },
  { code: 'DK', name: 'Dinamarca', flag: '🇩🇰' },
  { code: 'FI', name: 'Finlandia', flag: '🇫🇮' },
  { code: 'IE', name: 'Irlanda', flag: '🇮🇪' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'Nueva Zelanda', flag: '🇳🇿' },
  { code: 'JP', name: 'Japón', flag: '🇯🇵' },
  { code: 'KR', name: 'Corea del Sur', flag: '🇰🇷' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'AE', name: 'Emiratos Árabes Unidos', flag: '🇦🇪' },
  { code: 'SA', name: 'Arabia Saudita', flag: '🇸🇦' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'TR', name: 'Turquía', flag: '🇹🇷' },
  { code: 'ZA', name: 'Sudáfrica', flag: '🇿🇦' },
  { code: 'OTHER', name: 'Otro país', flag: '🌍' },
];

// Mapeo ISO2 → prefijo telefónico internacional (para geolocalización → +NN automático).
// Sincronizado 1:1 con PAYMENT_COUNTRIES.
const ISO_TO_PHONE_PREFIX = {
  CO: '+57', US: '+1', MX: '+52', ES: '+34', AR: '+54', CL: '+56', PE: '+51',
  EC: '+593', VE: '+58', PA: '+507', CR: '+506', GT: '+502', SV: '+503',
  HN: '+504', NI: '+505', BO: '+591', PY: '+595', UY: '+598', DO: '+1',
  PR: '+1', CU: '+53', CA: '+1', BR: '+55', GB: '+44', FR: '+33', DE: '+49',
  IT: '+39', PT: '+351', NL: '+31', BE: '+32', CH: '+41', AT: '+43',
  SE: '+46', NO: '+47', DK: '+45', FI: '+358', IE: '+353', AU: '+61',
  NZ: '+64', JP: '+81', KR: '+82', CN: '+86', IN: '+91', AE: '+971',
  SA: '+966', IL: '+972', TR: '+90', ZA: '+27',
};

// País -> moneda local (código ISO)
const COUNTRY_CURRENCY_MAP = {
  CO: 'COP', US: 'USD', MX: 'MXN', ES: 'EUR', AR: 'ARS', CL: 'CLP', PE: 'PEN',
  EC: 'USD', VE: 'VES', PA: 'USD', CR: 'CRC', GT: 'GTQ', SV: 'USD', HN: 'HNL',
  NI: 'NIO', BO: 'BOB', PY: 'PYG', UY: 'UYU', DO: 'DOP', PR: 'USD', CU: 'CUP',
  CA: 'CAD', BR: 'BRL', GB: 'GBP', FR: 'EUR', DE: 'EUR', IT: 'EUR', PT: 'EUR',
  NL: 'EUR', BE: 'EUR', CH: 'CHF', AT: 'EUR', SE: 'SEK', NO: 'NOK', DK: 'DKK',
  FI: 'EUR', IE: 'EUR', AU: 'AUD', NZ: 'NZD', JP: 'JPY', KR: 'KRW', CN: 'CNY',
  IN: 'INR', AE: 'AED', SA: 'SAR', IL: 'ILS', TR: 'TRY', ZA: 'ZAR', OTHER: 'USD',
};

// Helper para obtener headers de autenticación
const getAuthHeaders = () => {
  const token = localStorage.getItem('deisycfit_token');
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

const Checkout = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const location = useLocation();
  const [plan, setPlan] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [manualMethodsConfig, setManualMethodsConfig] = useState(null);
  const [selectedManualMethod, setSelectedManualMethod] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('mercadopago');

  // Auto-scroll: al elegir cualquier método, deslizar suave hasta el bloque de pago.
  const scrollToPayCta = () => {
    // Espera al render de las instrucciones/precio antes de deslizar
    setTimeout(() => {
      // Prioriza el bloque de instrucciones si es método manual
      const target =
        document.querySelector('[data-testid="manual-payment-instructions"]') ||
        document.querySelector('[data-testid="confirm-payment-button"]');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
  };
  const [selectedDuration, setSelectedDuration] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [showPaymentProof, setShowPaymentProof] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Guest checkout info (para usuarios sin cuenta)
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPassword, setGuestPassword] = useState('');
  const [guestPasswordConfirm, setGuestPasswordConfirm] = useState('');
  const [guestGender, setGuestGender] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestCountryCode, setGuestCountryCode] = useState('+57'); // Default Colombia
  const [guestIdentification, setGuestIdentification] = useState(''); // Cédula/DNI (opcional)
  
  // Promo code states
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [originalPrice, setOriginalPrice] = useState(0);
  
  // País del comprador: null = aún no elegido, código ISO ('CO', 'US', etc.) cuando seleccionado
  const [buyerCountry, setBuyerCountry] = useState(null);
  // País detectado por geolocalización (sugerencia, no obliga)
  const [detectedCountry, setDetectedCountry] = useState(null);
  // Buscador del selector de países
  const [countrySearch, setCountrySearch] = useState('');
  // Tasas de cambio desde USD a otras monedas (live, fetch al montar)
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, COP: 4000 });
  // Moneda derivada del país: Colombia -> COP, cualquier otro -> USD (precios mostrados)
  const selectedCurrency = buyerCountry === 'CO' ? 'COP' : 'USD';
  // Moneda local del país elegido (para mostrar el total convertido)
  const localCurrency = COUNTRY_CURRENCY_MAP[buyerCountry] || 'USD';

  // Detectar país por IP al montar.
  // Estrategia: probamos 2 servicios distintos para máxima confiabilidad.
  // Si ambos fallan, default = 'OTHER' (paga en USD). NUNCA dejamos al usuario elegir.
  useEffect(() => {
    const tryService = async (url, timeoutMs) => {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await fetch(url, { cache: 'no-store', signal: controller.signal });
        clearTimeout(t);
        if (!res.ok) return null;
        const data = await res.json();
        const code = (data?.country_code || data?.country || data?.countryCode || '').toUpperCase();
        return code || null;
      } catch (e) {
        clearTimeout(t);
        return null;
      }
    };
    const detectCountry = async () => {
      // 1) ipapi.co (principal)
      let code = await tryService('https://ipapi.co/json/', 4000);
      // 2) ipwho.is (respaldo)
      if (!code) code = await tryService('https://ipwho.is/', 4000);
      if (code) {
        setDetectedCountry(code);
        const exists = PAYMENT_COUNTRIES.some((c) => c.code === code);
        setBuyerCountry(exists ? code : 'OTHER');
        // Sincronizar prefijo telefónico automáticamente con el país detectado.
        // Solo si el usuario aún no cambió el prefijo manualmente (queda en +57 default).
        const phonePrefix = ISO_TO_PHONE_PREFIX[code];
        if (phonePrefix) {
          setGuestCountryCode((prev) => (prev === '+57' ? phonePrefix : prev));
        }
      } else {
        // Si fallan ambos servicios, asumimos OTHER (USD) — más seguro
        // que dejar elegir manualmente y arriesgar fraude de país.
        console.log('Geolocalización falló, default OTHER (USD)');
        setDetectedCountry('UNKNOWN');
        setBuyerCountry('OTHER');
      }
    };
    detectCountry();
  }, []);

  // Cargar tasas de cambio en vivo (USD -> otras monedas) al montar
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await fetch('https://open.er-api.com/v6/latest/USD', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.rates) {
          setExchangeRates({ USD: 1, ...data.rates });
        }
      } catch (err) {
        console.log('No se pudieron cargar tasas de cambio en vivo, usando fallback');
      }
    };
    fetchRates();
  }, []);

  // Auto-corregir la duración seleccionada si no hay precio para la moneda actual.
  // Selecciona la primera duración disponible con precio > 0.
  useEffect(() => {
    if (!plan || !buyerCountry) return;
    const isCOP = selectedCurrency === 'COP';
    const priceFor = (dur) => {
      const fld = isCOP
        ? { monthly: 'price_monthly_cop', bimonthly: 'price_bimonthly_cop', quarterly: 'price_quarterly_cop', semiannual: 'price_semiannual_cop', yearly: 'price_yearly_cop' }
        : { monthly: 'price_monthly', bimonthly: 'price_bimonthly', quarterly: 'price_quarterly', semiannual: 'price_semiannual', yearly: 'price_yearly' };
      return parseFloat(plan[fld[dur]] || 0);
    };
    if (priceFor(selectedDuration) > 0) return; // OK, ya hay precio
    // Buscar la primera con precio > 0
    const order = ['monthly', 'bimonthly', 'quarterly', 'semiannual', 'yearly'];
    const firstValid = order.find((d) => priceFor(d) > 0);
    if (firstValid && firstValid !== selectedDuration) {
      setSelectedDuration(firstValid);
    }
  }, [plan, buyerCountry, selectedCurrency, selectedDuration]);

  // Convierte un monto USD a la moneda local elegida
  const convertUSDtoLocal = (usdAmount) => {
    const rate = exchangeRates[localCurrency] || 1;
    return usdAmount * rate;
  };

  // Formatea un monto en una moneda específica
  const formatMoney = (amount, currency) => {
    if (currency === 'COP') {
      return `$${Math.round(amount).toLocaleString('es-CO')} COP`;
    }
    if (currency === 'USD') {
      // Mostrar 2 decimales si tiene parte decimal, sino entero
      const rounded = Math.round(amount * 100) / 100;
      return `$${rounded.toLocaleString('en-US', { minimumFractionDigits: rounded % 1 ? 2 : 0, maximumFractionDigits: 2 })} USD`;
    }
    // Otras monedas: redondear a entero (la mayoría no usa decimales en el display de checkout)
    return `$${Math.round(amount).toLocaleString()} ${currency}`;
  };

  const handleSelectCountry = (countryCode) => {
    setBuyerCountry(countryCode);
    setAppliedPromo(null); // Limpiar cupón si cambia de moneda
    setCountrySearch('');
  };

  const handleChangeCountry = () => {
    setBuyerCountry(null);
    setAppliedPromo(null);
    setCountrySearch('');
  };

  useEffect(() => {
    checkAuthAndFetchData();
  }, []);

  const checkAuthAndFetchData = async () => {
    try {
      // Verificar si el usuario está autenticado - primero localStorage
      const loggedIn = localStorage.getItem('deisycfit_logged_in');
      const savedUser = localStorage.getItem('deisycfit_user');
      
      if (loggedIn === 'true' && savedUser) {
        const userData = JSON.parse(savedUser);
        setIsAuthenticated(true);
        setGuestEmail(userData.email || '');
        setGuestName(userData.name || '');
        setGuestGender(userData.gender || '');
        setGuestPhone(userData.phone || '');
        setGuestCountryCode(userData.country_code || '+57');
      } else {
        // Intentar verificar con el backend - ENVIAR TOKEN DE AUTENTICACIÓN
        const token = localStorage.getItem('deisycfit_token');
        if (token) {
          try {
            const authRes = await axios.get(`${API}/user/profile`, { 
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: false 
            });
            if (authRes.data && authRes.data.email) {
              setIsAuthenticated(true);
              setGuestEmail(authRes.data.email);
              setGuestName(authRes.data.name || '');
              setGuestGender(authRes.data.gender || '');
              setGuestPhone(authRes.data.phone || '');
              setGuestCountryCode(authRes.data.country_code || '+57');
            }
          } catch (err) {
            console.log('No hay sesión activa, continuando como invitado');
            // Sin token válido, continuar como invitado (NO redirigir)
            setIsAuthenticated(false);
          }
        } else {
          // No hay token, continuar como invitado (NO redirigir al login)
          setIsAuthenticated(false);
        }
      }
    } catch (error) {
      console.error('Error en checkAuthAndFetchData:', error);
      // Error general - continuar como invitado
      setIsAuthenticated(false);
    }
    // Cargar los datos del checkout (para todos los usuarios)
    fetchData();
  };

  const fetchData = async () => {
    try {
      const [configRes] = await Promise.all([
        axios.get(`${API}/config`)
      ]);
      
      setPaymentMethods(configRes.data.payment_methods);
      // Métodos manuales editables por el admin (Nequi/Bancolombia/Transferencia/PayPal + WhatsApp)
      setManualMethodsConfig(configRes.data.manual_payment_methods || null);
      
      // Check for query params (ebooks, videos, recetarios, productos, renovación)
      const searchParams = new URLSearchParams(location.search);
      const planType = searchParams.get('plan');
      const itemId = searchParams.get('id');
      const isRenewal = searchParams.get('renewal') === '1';

      if (isRenewal) {
        // 🔄 Renovación de plan expirado — precio ya calculado por el backend
        let amount = parseFloat(searchParams.get('amount')) || 0;
        const period = searchParams.get('period') || 'monthly';
        const planId = planType || 'basic';
        const periodLabel = { monthly: 'Mensual', mensual: 'Mensual', bimonthly: 'Bimestral', bimestral: 'Bimestral', quarterly: 'Trimestral', trimestral: 'Trimestral', semester: 'Semestral', semiannual: 'Semestral', semestral: 'Semestral', yearly: 'Anual', annual: 'Anual', anual: 'Anual' }[String(period).toLowerCase()] || period;
        const planNames = { basic: 'Fit Evolution', basico: 'Fit Evolution', premium: 'Plan Premium', gold: 'Plan Gold VIP', vip_gold: 'Plan Gold VIP', competitivo: 'Plan Competitivo' };

        // ── Fallback anti-$0: si la URL no trajo `amount`, resolvemos el
        //    precio consultando /api/public/subscription-plans. Así evitamos
        //    que la pantalla muestre "Total: $0 COP".
        if (!amount || amount <= 0) {
          try {
            const plansRes = await axios.get(`${API}/public/subscription-plans`);
            const plans = plansRes.data?.plans || [];
            const target = String(planId).toLowerCase();
            const p = plans.find(pl => pl.slug === target || pl.id === target || pl.id === `plan_${target}`);
            if (p) {
              const isYear = String(period).toLowerCase().startsWith('year') || String(period).toLowerCase().startsWith('anu');
              const pricing = p.pricing || {};
              if (isYear) {
                amount = Number(p.price_yearly_cop) || Number(pricing.yearly?.cop) || 0;
                if (!amount) {
                  // Derivar de mensual COP con 40% off si no hay yearly.
                  const mCop = Number(p.price_monthly_cop) || Number(pricing.monthly?.cop) || 0;
                  amount = mCop ? Math.round(mCop * 12 * 0.6) : (Number(p.price_yearly) || Math.round(Number(p.price_monthly) * 12 * 0.6));
                }
              } else {
                amount = Number(p.price_monthly_cop) || Number(pricing.monthly?.cop) || Number(p.price_monthly) || Number(p.price) || 0;
              }
              amount = Math.round(Number(amount) || 0);
            }
          } catch (_e) {
            // silent — resolvePrice mostrará 0 y el usuario podrá pedir ayuda por WhatsApp.
          }
        }

        setPlan({
          id: `renewal-${planId}`,
          name: `Renovación · ${planNames[planId.toLowerCase()] || planId} (${periodLabel})`,
          type: 'renewal',
          renewalOf: planId,
          subscription_period: period,
          price: amount,
          price_display: `$${amount.toLocaleString('es-CO')} COP`,
          description: `Continúa tu ${planNames[planId.toLowerCase()] || planId}`,
        });
      } else if (planType === 'ebook' && itemId) {
        // Obtener datos del ebook de los query params
        const price = parseFloat(searchParams.get('price')) || 0;
        const originalPrice = parseFloat(searchParams.get('originalPrice')) || price;
        const title = searchParams.get('title') || 'Ebook';
        const discount = searchParams.get('discount') || '0%';
        
        setPlan({
          id: `ebook-${itemId}`,
          name: title,
          type: 'ebook',
          itemId: itemId,
          price: price,
          price_display: `$${price.toLocaleString('es-CO')} COP`,
          original_price: originalPrice,
          discount: discount,
          description: discount !== '0%' 
            ? `¡Ahorra ${discount}! Precio original: $${originalPrice}` 
            : 'Compra individual de ebook'
        });
      } else if (planType === 'recetario' && itemId) {
        // Obtener datos del recetario de los query params
        const price = parseFloat(searchParams.get('price')) || 0;
        const originalPrice = parseFloat(searchParams.get('originalPrice')) || price;
        const title = searchParams.get('title') || 'Recetario en Video';
        const discount = searchParams.get('discount') || '0%';
        
        setPlan({
          id: `recetario-${itemId}`,
          name: title,
          type: 'recetario',
          itemId: itemId,
          price: price,
          price_display: `$${price.toLocaleString('es-CO')} COP`,
          original_price: originalPrice,
          discount: discount,
          description: discount !== '0%' 
            ? `¡Ahorra ${discount}! Precio original: $${originalPrice}` 
            : 'Acceso completo al recetario en video'
        });
      } else if (planType === 'producto' && itemId) {
        // Obtener datos del producto de tienda
        const price = parseFloat(searchParams.get('price')) || 0;
        const originalPrice = parseFloat(searchParams.get('originalPrice')) || price;
        const title = searchParams.get('title') || 'Producto';
        const discount = searchParams.get('discount') || '0%';
        
        setPlan({
          id: `producto-${itemId}`,
          name: title,
          type: 'producto',
          itemId: itemId,
          price: price,
          price_display: `$${price.toLocaleString('es-CO')} COP`,
          original_price: originalPrice,
          discount: discount,
          description: discount !== '0%' 
            ? `¡Ahorra ${discount}! Precio original: $${originalPrice}` 
            : 'Producto de la tienda'
        });
      } else if (planType === 'video') {
        // Crear plan ficticio para acceso a videos
        const userProfile = await axios.get(`${API}/user/profile`, { withCredentials: false });
        const userPlan = userProfile.data.subscription_plan || 'none';
        const price = userPlan === 'basic' ? 5 : 7;
        
        setPlan({
          id: 'video-access',
          name: 'Acceso Mensual a Videos',
          type: 'video',
          price: price,
          price_display: `$${price}/mes`,
          description: userPlan === 'basic' 
            ? '¡Precio especial con tu descuento!' 
            : 'Acceso completo a toda la biblioteca de videos'
        });
      } else if (planType) {
        // Viene de la landing con ?plan=xxx&period=xxx
        const period = searchParams.get('period') || 'monthly';
        setSelectedDuration(period);
        
        // Buscar el plan en subscription-plans
        try {
          const plansRes = await axios.get(`${API}/public/subscription-plans`);
          const plans = plansRes.data.plans || [];
          
          // Aliases amigables para URLs públicas
          const FRIENDLY_QS_MAP = {
            'fit-evolution': ['basic', 'plan_basic'],
            'fitevolution': ['basic', 'plan_basic'],
          };
          const qsAliases = FRIENDLY_QS_MAP[(planType || '').toLowerCase()] || [];

          // Buscar por slug o nombre
          const foundPlan = plans.find(p => 
            p.slug === planType || 
            p.id === planType ||
            p.name.toLowerCase().replace(/\s+/g, '-') === planType ||
            p.name.toLowerCase().includes(planType.toLowerCase()) ||
            qsAliases.includes(p.id) || qsAliases.includes(p.slug)
          );
          
          if (foundPlan) {
            setPlan({
              ...foundPlan,
              type: 'subscription'
            });
          } else {
            toast.error('Plan no encontrado');
            navigate('/');
          }
        } catch (err) {
          console.error('Error fetching subscription plan:', err);
          toast.error('Error al cargar el plan');
        }
      } else if (location.state && location.state.plan) {
        const receivedPlan = location.state.plan;
        setPlan(receivedPlan);
        // Set default duration for Premium plans
        if (receivedPlan.price_monthly) {
          setSelectedDuration('monthly');
        }
      } else if (planId) {
        // Fallback: buscar el plan por ID en ambos endpoints (refresh, link compartido)
        let foundPlan = null;
        // Aliases amigables usados en URLs públicas → id/slug interno
        const FRIENDLY_SLUG_MAP = {
          'fit-evolution': ['basic', 'plan_basic'],
          'fitevolution': ['basic', 'plan_basic'],
        };
        const aliases = FRIENDLY_SLUG_MAP[(planId || '').toLowerCase()] || [];
        const matchPlan = (p) => p.id === planId || p.slug === planId || aliases.includes(p.id) || aliases.includes(p.slug);
        // 1. Intentar /api/public/subscription-plans (fuente moderna)
        try {
          const plansRes = await axios.get(`${API}/public/subscription-plans`);
          const plans = plansRes.data?.plans || [];
          foundPlan = plans.find(matchPlan);
        } catch (e) {
          // ignorar y probar el siguiente
        }
        // 2. Si no se encontró, probar /api/plans (legacy)
        if (!foundPlan) {
          try {
            const planRes = await axios.get(`${API}/plans`);
            foundPlan = (planRes.data || []).find(matchPlan);
          } catch (e) {
            // ignorar
          }
        }
        if (foundPlan) {
          setPlan({ ...foundPlan, type: foundPlan.type || 'subscription' });
          if (foundPlan.price_monthly) {
            setSelectedDuration('monthly');
          }
        } else {
          toast.error('Plan no encontrado, vuelve a seleccionar uno');
          setTimeout(() => navigate('/plans'), 1500);
        }
      } else {
        // Sin params, sin state, sin planId → no hay forma de saber qué plan quiere
        toast.error('Selecciona un plan primero');
        setTimeout(() => navigate('/plans'), 1500);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // ESTRUCTURA DE PRECIOS: Fuente única de verdad
  // Resuelve el precio según: plan + duración + moneda
  // ============================================================

  const resolvePrice = (currency) => {
    if (!plan) return 0;

    // 🔄 Renovación: el precio ya viene calculado en plan.price (query param `amount`)
    // No usar la matriz de precios por período — ese precio ya considera el plan/período.
    if (plan.type === 'renewal') {
      if (currency === 'COP') return plan.price || 0;
      // En USD dividimos por la tasa de cambio; si no disponible, escalar por 4200
      if (currency === 'USD') {
        const rate = (exchangeRates && exchangeRates.COP) || 4000;
        return plan.price ? Math.max(1, plan.price / rate) : 0;
      }
      return plan.price || 0;
    }

    if (currency === 'COP') {
      // 1. Buscar campos explícitos _cop
      if (plan.price_monthly_cop || plan.price_yearly_cop) {
        switch (selectedDuration) {
          case 'monthly': return plan.price_monthly_cop || 0;
          case 'bimonthly': return plan.price_bimonthly_cop || 0;
          case 'quarterly': return plan.price_quarterly_cop || 0;
          case 'semiannual': return plan.price_semiannual_cop || 0;
          case 'yearly': return plan.price_yearly_cop || 0;
          default: return plan.price_monthly_cop || plan.price_yearly_cop || 0;
        }
      }
      // 2. Buscar price_cop genérico
      if (plan.price_cop) return plan.price_cop;
      // 3. Buscar en pricing anidado
      if (plan.pricing) {
        const key = { monthly: 'monthly', bimonthly: 'bimonthly', quarterly: 'quarterly', semiannual: 'semester', yearly: 'yearly' }[selectedDuration] || 'monthly';
        if (plan.pricing[key]?.cop) return plan.pricing[key].cop;
      }
      // 4. FALLBACK: Si el plan tiene currency=COP y price_monthly es un valor COP (>1000),
      //    entonces price_monthly ES un precio COP, no USD
      if (plan.currency === 'COP' && plan.price_monthly && plan.price_monthly >= 1000) {
        switch (selectedDuration) {
          case 'monthly': return plan.price_monthly || 0;
          case 'bimonthly': return plan.price_bimonthly || 0;
          case 'quarterly': return plan.price_quarterly || 0;
          case 'semiannual': return plan.price_semiannual || 0;
          case 'yearly': return plan.price_yearly || 0;
          default: return plan.price_monthly || plan.price_yearly || 0;
        }
      }
      return 0;
    }
    
    if (currency === 'USD') {
      if (plan.price_monthly || plan.price_yearly) {
        // Solo usar price_monthly como USD si el valor es < 1000 (precio típico USD)
        // Si es >= 1000, probablemente es COP mal categorizado
        const monthlyVal = plan.price_monthly || 0;
        if (monthlyVal > 0 && monthlyVal < 1000) {
          switch (selectedDuration) {
            case 'monthly': return plan.price_monthly || 0;
            case 'bimonthly': return plan.price_bimonthly || 0;
            case 'quarterly': return plan.price_quarterly || 0;
            case 'semiannual': return plan.price_semiannual || 0;
            case 'yearly': return plan.price_yearly || 0;
            default: return plan.price_monthly || plan.price_yearly || 0;
          }
        }
      }
      if (plan.price && plan.price < 1000) return plan.price;
      return 0;
    }
    
    return 0;
  };

  // ============================================================
  // CÁLCULO DE PRECIOS CON DESCUENTO
  // ============================================================

  // Precio base según moneda seleccionada (sin descuento)
  const getBasePrice = () => resolvePrice(selectedCurrency);
  
  // Monto del descuento según moneda seleccionada
  const getDiscountAmount = () => {
    if (!appliedPromo || !appliedPromo.discount_amount) return 0;
    // El descuento ya fue calculado por el backend en la moneda correcta
    return appliedPromo.discount_amount;
  };

  // Precio final = base - descuento (lo que se envía a Mercado Pago)
  const getFinalPrice = () => {
    if (appliedPromo && appliedPromo.final_price > 0) {
      return appliedPromo.final_price;
    }
    return getBasePrice();
  };

  const getDurationLabel = () => {
    switch (selectedDuration) {
      case 'monthly':
        return 'Mensual';
      case 'bimonthly':
        return 'Bimestral (2 meses)';
      case 'quarterly':
        return 'Trimestral (3 meses)';
      case 'semiannual':
        return 'Semestral (6 meses)';
      case 'yearly':
        return 'Anual (12 meses)';
      default:
        return '';
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Ingresa un código promocional');
      return;
    }

    setPromoLoading(true);
    try {
      // Precio base según moneda seleccionada
      const basePrice = resolvePrice(selectedCurrency);
      const itemType = plan.type || 'plans';
      
      if (basePrice <= 0) {
        toast.error('No hay precio disponible para esta moneda');
        setPromoLoading(false);
        return;
      }

      const response = await axios.post(`${API}/validate-promo-code`, {
        code: promoCode,
        price: basePrice,
        currency: selectedCurrency,
        item_type: itemType
      }, { withCredentials: false });

      if (response.data.valid) {
        setAppliedPromo({
          code: promoCode,
          discount_amount: response.data.discount_amount,
          final_price: response.data.final_price,
          currency: response.data.currency || selectedCurrency,
          promo_code_id: response.data.promo_code_id
        });
        setOriginalPrice(basePrice);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código no válido');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setOriginalPrice(0);
  };

  const handlePayment = async () => {
    if (!selectedMethod) {
      toast.error('Selecciona un método de pago');
      return;
    }

    // Validaciones para usuarios invitados (no autenticados)
    if (!isAuthenticated) {
      // Validar nombre
      if (!guestName || guestName.trim().length < 2) {
        toast.error('Ingresa tu nombre completo');
        return;
      }
      
      // Validar email
      if (!guestEmail) {
        toast.error('Ingresa tu correo electrónico para continuar');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
        toast.error('Ingresa un correo electrónico válido');
        return;
      }
      
      // Validar contraseña
      if (!guestPassword || guestPassword.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (guestPassword !== guestPasswordConfirm) {
        toast.error('Las contraseñas no coinciden');
        return;
      }
      
      // Validar género
      if (!guestGender) {
        toast.error('Selecciona tu género');
        return;
      }
      
      // Validar teléfono
      if (!guestPhone || guestPhone.length < 7) {
        toast.error('Ingresa un número de teléfono válido');
        return;
      }
    }

    // ============================================================
    // RESOLUCIÓN DE PRECIO - fuente única de verdad
    // ============================================================
    const basePrice = resolvePrice(selectedCurrency);
    const discountAmount = getDiscountAmount();
    const finalPrice = getFinalPrice();
    
    // Validaciones obligatorias
    if (!selectedCurrency || !['COP', 'USD'].includes(selectedCurrency)) {
      toast.error('Moneda no seleccionada');
      return;
    }
    if (typeof basePrice !== 'number' || isNaN(basePrice) || basePrice <= 0) {
      toast.error('Precio base inválido para esta moneda');
      return;
    }
    if (typeof finalPrice !== 'number' || isNaN(finalPrice) || finalPrice <= 0) {
      toast.error('Precio final inválido');
      return;
    }
    
    // Si hay cupón aplicado en otra moneda, limpiar
    if (appliedPromo && appliedPromo.currency && appliedPromo.currency !== selectedCurrency) {
      toast.error('El cupón fue aplicado en otra moneda. Aplícalo de nuevo.');
      setAppliedPromo(null);
      return;
    }
    
    // ============================================================
    // CONVERSIÓN A COP PARA MERCADO PAGO
    // - Colombia: ya está en COP, se envía igual
    // - Otros países: el precio mostrado es USD, lo convertimos a COP usando tasa en vivo
    //   Mercado Pago siempre cobra en COP (moneda del comerciante).
    //   El banco/tarjeta del comprador hace la conversión a su moneda local.
    // ============================================================
    const isColombia = buyerCountry === 'CO';
    const amountToCharge = isColombia
      ? finalPrice
      : Math.round(finalPrice * (exchangeRates.COP || 4000));
    const currencyToCharge = 'COP';
    
    // Log de debug completo
    console.log('MercadoPago Checkout Debug', {
      buyerCountry,
      selectedCurrency,
      basePrice,
      discountAmount,
      finalPrice,
      amountToCharge,
      currencyToCharge,
      appliedPromo,
      planName: plan?.name,
      duration: selectedDuration
    });

    // Determinar el tipo de producto
    const productType = plan?.type || 'subscription';
    
    // Datos del comprador (invitado o autenticado)
    const buyerEmail = guestEmail;
    const buyerName = guestName || 'Cliente';

    try {
      // Obtener headers de autenticación
      const authHeaders = getAuthHeaders();
      
      const paymentData = {
        plan_id: productType === 'subscription' ? (plan?.id || planId) : null,
        plan_type: plan?.plan_type || plan?.slug || plan?.id || planId,
        plan_name: plan?.name || 'Plan DeisyCFit',
        subscription_period: selectedDuration || 'monthly',
        ebook_id: productType === 'ebook' ? plan?.itemId : null,
        recetario_id: productType === 'recetario' ? plan?.itemId : null,
        producto_id: productType === 'producto' ? plan?.itemId : null,
        product_type: productType,
        amount: amountToCharge,
        currency_id: currencyToCharge,
        payment_method: selectedMethod,
        duration: selectedDuration,
        promo_code_id: appliedPromo?.promo_code_id || null,
        title: plan?.name || 'Compra DeisyCFit',
        guest_email: buyerEmail,
        guest_name: buyerName
      };
      
      // Si es invitado, agregar datos para crear cuenta después del pago
      if (!isAuthenticated) {
        paymentData.guest_password = guestPassword;
        paymentData.guest_gender = guestGender;
        paymentData.guest_phone = guestPhone;
        paymentData.guest_country_code = guestCountryCode;
        paymentData.guest_identification = guestIdentification;
      }
      
      const endpoint = `${API}/payments/mercadopago/create`;
      
      const response = await axios.post(endpoint, paymentData, { headers: authHeaders });

      if (response.data.success && (response.data.redirect_url || response.data.init_point)) {
        // GUARDAR referencia de pago en localStorage como respaldo
        // En caso de que MercadoPago no devuelva external_reference en la URL de retorno
        if (response.data.preference_id) {
          localStorage.setItem('deisycfit_pending_preference_id', response.data.preference_id);
        }
        localStorage.setItem('deisycfit_pending_payment_email', buyerEmail);
        localStorage.setItem('deisycfit_pending_payment_time', Date.now().toString());
        
        window.location.href = response.data.redirect_url || response.data.init_point;
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message;
      console.error('ERROR REAL MERCADOPAGO', {
        message: error.message,
        status: error.response?.status,
        detail: errorDetail,
        responseData: error.response?.data,
        sentPayload: {
          amount: finalPrice,
          currency_id: selectedCurrency,
          typeOfAmount: typeof finalPrice,
          isNaN: Number.isNaN(finalPrice)
        }
      });
      toast.error('Error al procesar el pago: ' + errorDetail);
    }
  };

  const handleSubmitProof = async () => {
    try {
      // Obtener headers de autenticación
      const authHeaders = getAuthHeaders();
      
      // Determinar el tipo de producto
      const productType = plan?.type || 'subscription';
      
      const response = await axios.post(`${API}/payments/create`, {
        plan_id: productType === 'subscription' ? (plan?.id || planId) : null,
        ebook_id: productType === 'ebook' ? plan?.itemId : null,
        recetario_id: productType === 'recetario' ? plan?.itemId : null,
        producto_id: productType === 'producto' ? plan?.itemId : null,
        product_type: productType,
        payment_method: selectedMethod,
        amount: getFinalPrice(),
        duration: plan?.price_monthly ? selectedDuration : plan?.duration || 'monthly'
      }, { headers: authHeaders });

      // Mark payment as completed (in real app, admin would verify)
      await axios.post(`${API}/payments/verify/${response.data.purchase_id}`, {}, { headers: authHeaders });

      toast.success('¡Pago registrado! Verificaremos tu comprobante pronto.');
      
      // Check if plan has WhatsApp access (Premium or Gold)
      if (planId && (planId.includes('premium') || planId.includes('gold'))) {
        setTimeout(() => {
          toast.info('Redirigiendo a WhatsApp para comenzar tu asesoría...');
          window.open('https://wa.link/k921x8', '_blank');
          setTimeout(() => navigate('/dashboard'), 1000);
        }, 2000);
      } else {
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (error) {
      toast.error('Error al procesar el pago');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div 
          className="w-16 h-16 rounded-full animate-pulse"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
        />
      </div>
    );
  }

  // ========================================================
  // GUARDA: si después de cargar no hay plan (link compartido sin params,
  // refresh sin state, etc.), mostrar mensaje claro y botón a /plans
  // en vez de un total $0 confuso.
  // ========================================================
  if (!plan) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-6 py-12 relative"
        style={{ backgroundColor: '#0a0a0a' }}
        data-testid="no-plan-screen"
      >
        <div 
          className="rounded-2xl backdrop-blur-xl overflow-hidden p-8 md:p-10 text-center max-w-md w-full"
          style={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <div className="text-5xl mb-4">🛒</div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
            No hay un plan seleccionado
          </h1>
          <p className="text-gray-400 mb-6">
            Vuelve a la página de planes y elige el que quieras comprar.
          </p>
          <Button 
            onClick={() => navigate('/plans')}
            className="w-full text-white font-semibold rounded-full py-5"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
            data-testid="go-to-plans-button"
          >
            Ver Planes
          </Button>
        </div>
      </div>
    );
  }

  // ========================================================
  // PANTALLA DE DETECCIÓN DE PAÍS (mientras la geolocalización resuelve)
  // El usuario NUNCA elige país manualmente: lo decide la geolocalización por IP.
  // Si la API falla, useEffect ya setea buyerCountry='OTHER' como fallback seguro.
  // ========================================================
  if (!buyerCountry) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-6 py-12 relative"
        style={{ backgroundColor: '#0a0a0a' }}
        data-testid="country-detecting-screen"
      >
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
          }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 text-center"
        >
          <div 
            className="w-16 h-16 rounded-full animate-pulse mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
          />
          <p className="text-white text-lg font-semibold">Detectando tu ubicación...</p>
          <p className="text-gray-400 text-sm mt-1">Para mostrarte el precio en tu moneda</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-12 px-6 relative"
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
        }}
      />
      <div className="relative z-10 max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/#planes')} 
          className="mb-6 text-gray-400 hover:text-white" 
          data-testid="back-button"
        >
          <FaArrowLeft className="mr-2" /> Volver
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 
            className="text-4xl lg:text-5xl font-bold text-white mb-4 uppercase tracking-tight" 
            style={{ fontFamily: 'Poppins, sans-serif' }}
            data-testid="checkout-title"
          >
            Finalizar Compra
          </h1>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div 
              className="rounded-2xl backdrop-blur-xl overflow-hidden"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div className="p-6 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                <h2 className="text-xl font-bold text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Resumen de Compra
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="pb-4 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <div>
                      <div className="font-semibold text-lg text-white">{plan?.name}</div>
                      <div className="text-sm text-gray-400 mt-1 whitespace-pre-line">{plan?.description}</div>
                    </div>
                  </div>

                  {/* Beneficios del plan elegido */}
                  {Array.isArray(plan?.features) && plan.features.length > 0 && (
                    <div
                      className="rounded-xl p-4"
                      style={{
                        background: 'rgba(var(--brand-primary-glow), 0.06)',
                        border: '1px solid rgba(var(--brand-primary-glow), 0.25)'
                      }}
                      data-testid="checkout-plan-benefits"
                    >
                      <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-primary)', fontFamily: 'Poppins, sans-serif' }}>
                        ¿Qué incluye este plan?
                      </div>
                      <ul className="space-y-2">
                        {plan.features.map((feat, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-200" data-testid={`checkout-feature-${idx}`}>
                            <span style={{ color: 'var(--brand-primary)' }} className="mt-0.5 flex-shrink-0">✓</span>
                            <span className="leading-relaxed">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Duration Selector for subscription plans */}
                  {(plan?.price_monthly || plan?.price_yearly || plan?.price_monthly_cop || plan?.price_yearly_cop) && (
                    <div className="space-y-3">
                      <div className="font-semibold text-sm text-gray-300">Selecciona la duración:</div>
                      <div className="space-y-2">
                        {(selectedCurrency === 'COP' ? plan.price_monthly_cop > 0 : plan.price_monthly > 0) && (
                          <div 
                            className={`p-4 rounded-xl cursor-pointer transition-all ${
                              selectedDuration === 'monthly' ? 'shadow-lg' : ''
                            }`}
                            style={{ 
                              background: selectedDuration === 'monthly' ? 'rgba(var(--brand-primary-glow), 0.2)' : 'rgba(255, 255, 255, 0.05)',
                              border: selectedDuration === 'monthly' ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                            onClick={() => setSelectedDuration('monthly')}
                            data-testid="duration-monthly"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-semibold text-white">Mensual</div>
                                <div className="text-xs text-gray-400">Pago cada mes</div>
                              </div>
                              <div className="text-right">
                                {selectedCurrency === 'COP' ? (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${(plan.price_monthly_cop || plan.price_cop || 0).toLocaleString('es-CO')} COP</div>
                                ) : (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${plan.price_monthly} USD</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {(selectedCurrency === 'COP' ? plan.price_bimonthly_cop > 0 : plan.price_bimonthly > 0) && (
                          <div 
                            className={`p-4 rounded-xl cursor-pointer transition-all ${
                              selectedDuration === 'bimonthly' ? 'shadow-lg' : ''
                            }`}
                            style={{ 
                              background: selectedDuration === 'bimonthly' ? 'rgba(var(--brand-primary-glow), 0.2)' : 'rgba(255, 255, 255, 0.05)',
                              border: selectedDuration === 'bimonthly' ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                            onClick={() => setSelectedDuration('bimonthly')}
                            data-testid="duration-bimonthly"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-semibold text-white">Bimestral</div>
                                <div className="text-xs text-gray-400">Pago cada 2 meses</div>
                              </div>
                              <div className="text-right">
                                {selectedCurrency === 'COP' ? (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${(plan.price_bimonthly_cop || 0).toLocaleString('es-CO')} COP</div>
                                ) : (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${plan.price_bimonthly} USD</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {(selectedCurrency === 'COP' ? plan.price_quarterly_cop > 0 : plan.price_quarterly > 0) && (
                          <div 
                            className={`p-4 rounded-xl cursor-pointer transition-all ${
                              selectedDuration === 'quarterly' ? 'shadow-lg' : ''
                            }`}
                            style={{ 
                              background: selectedDuration === 'quarterly' ? 'rgba(var(--brand-primary-glow), 0.2)' : 'rgba(255, 255, 255, 0.05)',
                              border: selectedDuration === 'quarterly' ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                            onClick={() => setSelectedDuration('quarterly')}
                            data-testid="duration-quarterly"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-semibold text-white">Trimestral</div>
                                <div className="text-xs text-gray-400">Pago cada 3 meses</div>
                              </div>
                              <div className="text-right">
                                {selectedCurrency === 'COP' ? (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${(plan.price_quarterly_cop || 0).toLocaleString('es-CO')} COP</div>
                                ) : (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${plan.price_quarterly} USD</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {(selectedCurrency === 'COP' ? plan.price_semiannual_cop > 0 : plan.price_semiannual > 0) && (
                          <div 
                            className={`p-4 rounded-xl cursor-pointer transition-all ${
                              selectedDuration === 'semiannual' ? 'shadow-lg' : ''
                            }`}
                            style={{ 
                              background: selectedDuration === 'semiannual' ? 'rgba(var(--brand-primary-glow), 0.2)' : 'rgba(255, 255, 255, 0.05)',
                              border: selectedDuration === 'semiannual' ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                            onClick={() => setSelectedDuration('semiannual')}
                            data-testid="duration-semiannual"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-semibold text-white">Semestral</div>
                                <div className="text-xs text-gray-400">Pago cada 6 meses - Ahorra más</div>
                              </div>
                              <div className="text-right">
                                {selectedCurrency === 'COP' ? (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${(plan.price_semiannual_cop || 0).toLocaleString('es-CO')} COP</div>
                                ) : (
                                  <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${plan.price_semiannual} USD</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {(selectedCurrency === 'COP' ? plan.price_yearly_cop > 0 : plan.price_yearly > 0) && (
                          <div 
                            className={`p-4 rounded-xl cursor-pointer transition-all relative ${
                              selectedDuration === 'yearly' ? 'shadow-lg' : ''
                            }`}
                            style={{ 
                              background: selectedDuration === 'yearly' ? 'rgba(var(--brand-primary-glow), 0.2)' : 'rgba(255, 255, 255, 0.05)',
                              border: selectedDuration === 'yearly' ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                            onClick={() => setSelectedDuration('yearly')}
                            data-testid="duration-yearly"
                          >
                            <div className="absolute -top-2 -right-2 text-white text-xs px-2 py-1 rounded-full font-bold" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>
                              {(() => {
                                // Calcular ahorro dinámicamente: mensual x 12 vs anual
                                const monthly = selectedCurrency === 'COP' ? (plan.price_monthly_cop || 0) : (plan.price_monthly || 0);
                                const yearly = selectedCurrency === 'COP' ? (plan.price_yearly_cop || 0) : (plan.price_yearly || 0);
                                if (monthly > 0 && yearly > 0) {
                                  const pct = Math.round(((monthly * 12 - yearly) / (monthly * 12)) * 100);
                                  if (pct > 0) return `🔥 AHORRA ${pct}%`;
                                }
                                return '🔥 MEJOR PRECIO';
                              })()}
                            </div>
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-semibold text-white">Anual</div>
                                <div className="text-xs text-gray-400">Pago único - Máximo ahorro</div>
                              </div>
                              <div className="text-right">
                                {selectedCurrency === 'COP' ? (
                                  <>
                                    {(() => {
                                      const monthly = plan.price_monthly_cop || 0;
                                      const equiv = monthly * 12;
                                      if (equiv > 0 && equiv > (plan.price_yearly_cop || 0)) {
                                        return <div className="text-[11px] text-gray-500 line-through">${equiv.toLocaleString('es-CO')} COP</div>;
                                      }
                                      return null;
                                    })()}
                                    <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${(plan.price_yearly_cop || 0).toLocaleString('es-CO')} COP</div>
                                  </>
                                ) : (
                                  <>
                                    {(() => {
                                      const monthly = plan.price_monthly || 0;
                                      const equiv = monthly * 12;
                                      if (equiv > 0 && equiv > (plan.price_yearly || 0)) {
                                        return <div className="text-[11px] text-gray-500 line-through">${equiv.toFixed(2)} USD</div>;
                                      }
                                      return null;
                                    })()}
                                    <div className="font-bold" style={{ color: 'var(--brand-primary)' }}>${plan.price_yearly} USD</div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Indicador de país detectado por geolocalización (sin opción de cambiar) */}
                  <div 
                    className="pt-4 border-t flex items-center justify-between gap-3 flex-wrap"
                    style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    {(() => {
                      const countryInfo = PAYMENT_COUNTRIES.find(c => c.code === buyerCountry) || PAYMENT_COUNTRIES.find(c => c.code === 'OTHER');
                      return (
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{countryInfo.flag}</span>
                          <div>
                            <div className="text-xs text-gray-400">Pagas desde</div>
                            <div className="font-bold text-white text-sm" data-testid="buyer-country-label">
                              {countryInfo.name} ({selectedCurrency})
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <span className="text-xs text-gray-500">📍 Detectado automáticamente</span>
                  </div>

                  {/* Promo Code Section */}
                  <div className="pt-4 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <div className="font-semibold text-sm text-gray-300 mb-2">🎫 ¿Tienes un código promocional?</div>
                    {!appliedPromo ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ingresa tu código"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          disabled={promoLoading}
                          className="flex-1"
                        />
                        <Button 
                          onClick={handleApplyPromoCode}
                          disabled={promoLoading || !promoCode.trim()}
                          variant="outline"
                          className="whitespace-nowrap"
                        >
                          {promoLoading ? 'Validando...' : 'Aplicar'}
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-bold text-green-800">Codigo: {appliedPromo.code}</div>
                            <div className="text-sm text-green-700">
                              Descuento: ${getDiscountAmount().toLocaleString(selectedCurrency === 'COP' ? 'es-CO' : 'en-US')} {selectedCurrency}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={handleRemovePromoCode}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          >
                            ✖ Quitar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Price Summary */}
                  {appliedPromo && (
                    <div className="space-y-2 pt-2">
                      <div className="flex justify-between items-center text-gray-400">
                        <span>Precio original:</span>
                        <span className="line-through">${getBasePrice().toLocaleString(selectedCurrency === 'COP' ? 'es-CO' : 'en-US')} {selectedCurrency}</span>
                      </div>
                      <div className="flex justify-between items-center text-green-400 font-semibold">
                        <span>Descuento:</span>
                        <span>-${getDiscountAmount().toLocaleString(selectedCurrency === 'COP' ? 'es-CO' : 'en-US')} {selectedCurrency}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 pt-4 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    {(() => {
                      const finalPrice = getFinalPrice();
                      const isColombia = buyerCountry === 'CO';
                      // Para Colombia: el precio ya es COP
                      // Para extranjeros: finalPrice es USD; convertimos a su moneda local
                      const localAmount = isColombia ? finalPrice : convertUSDtoLocal(finalPrice);
                      // Monto que cobrará Mercado Pago (siempre en COP)
                      const copToCharge = isColombia
                        ? finalPrice
                        : Math.round(finalPrice * (exchangeRates.COP || 4000));
                      const totalColor = isColombia ? '#22c55e' : '#3b82f6';

                      return (
                        <>
                          <div className="flex justify-between items-center text-xl font-bold">
                            <div>
                              <div className="text-white">Total a pagar:</div>
                              {(plan?.price_monthly || plan?.price_yearly) && (
                                <div className="text-sm font-normal text-gray-400">{getDurationLabel()}</div>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-2xl" style={{ color: totalColor }} data-testid="total-amount">
                                {formatMoney(localAmount, localCurrency)}
                              </span>
                              {!isColombia && localCurrency !== 'USD' && (
                                <div className="text-xs text-gray-500 mt-1">
                                  ({formatMoney(finalPrice, 'USD')})
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="p-3 rounded-lg text-center" style={{ 
                            backgroundColor: 'rgba(0, 158, 227, 0.1)',
                            border: '1px solid rgba(0, 158, 227, 0.3)'
                          }}>
                            <p className="text-sm" style={{ color: '#60a5fa' }}>
                              💳 Mercado Pago cobrará {formatMoney(copToCharge, 'COP')}
                            </p>
                            {!isColombia && (
                              <p className="text-xs text-gray-400 mt-1">
                                Tu banco hará la conversión a tu moneda local automáticamente
                              </p>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Warning if price is 0 */}
                  {getBasePrice() === 0 && plan?.type !== 'renewal' && (
                    <div className="rounded-lg p-3 mt-4" style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
                      <p className="text-amber-400 text-sm font-medium">
                        ⚠️ El precio del período seleccionado no está configurado. Por favor selecciona otra duración o contacta a soporte.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Methods */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div 
              className="rounded-2xl backdrop-blur-xl overflow-hidden"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* Formulario de registro para invitados */}
              {!isAuthenticated && (
                <div className="p-6 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                  <h2 className="text-xl font-bold text-white uppercase mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Crea tu cuenta
                  </h2>
                  <p className="text-sm text-gray-400 mb-4">Tu cuenta se activará después del pago</p>
                  
                  <div className="space-y-4">
                    {/* Nombre */}
                    <div>
                      <Label className="text-gray-300 text-sm mb-1 flex items-center gap-2">
                        <FaUser className="text-pink-400" /> Nombre completo *
                      </Label>
                      <Input
                        type="text"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="Tu nombre completo"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        data-testid="guest-name-input"
                      />
                    </div>
                    
                    {/* Email */}
                    <div>
                      <Label className="text-gray-300 text-sm mb-1 flex items-center gap-2">
                        <FaEnvelope className="text-pink-400" /> Correo electrónico *
                      </Label>
                      <Input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="tu@correo.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        data-testid="guest-email-input"
                      />
                    </div>
                    
                    {/* Contraseña */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-300 text-sm mb-1 flex items-center gap-2">
                          <FaLock className="text-pink-400" /> Contraseña *
                        </Label>
                        <Input
                          type="password"
                          value={guestPassword}
                          onChange={(e) => setGuestPassword(e.target.value)}
                          placeholder="Min. 6 caracteres"
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                          data-testid="guest-password-input"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300 text-sm mb-1">Confirmar *</Label>
                        <Input
                          type="password"
                          value={guestPasswordConfirm}
                          onChange={(e) => setGuestPasswordConfirm(e.target.value)}
                          placeholder="Repetir contraseña"
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                          data-testid="guest-password-confirm-input"
                        />
                      </div>
                    </div>
                    
                    {/* Género */}
                    <div>
                      <Label className="text-gray-300 text-sm mb-1 flex items-center gap-2">
                        <FaVenusMars className="text-pink-400" /> Género *
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div
                          className={`p-3 rounded-xl cursor-pointer transition-all text-center ${guestGender === 'female' ? 'ring-2 ring-pink-500' : ''}`}
                          style={{ 
                            background: guestGender === 'female' ? 'rgba(var(--brand-primary-glow), 0.2)' : 'rgba(255, 255, 255, 0.05)',
                            border: guestGender === 'female' ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                          onClick={() => setGuestGender('female')}
                          data-testid="gender-female"
                        >
                          <span className="text-2xl">👩</span>
                          <div className="text-sm text-white mt-1">Mujer</div>
                        </div>
                        <div
                          className={`p-3 rounded-xl cursor-pointer transition-all text-center ${guestGender === 'male' ? 'ring-2 ring-blue-500' : ''}`}
                          style={{ 
                            background: guestGender === 'male' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                            border: guestGender === 'male' ? '2px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                          onClick={() => setGuestGender('male')}
                          data-testid="gender-male"
                        >
                          <span className="text-2xl">👨</span>
                          <div className="text-sm text-white mt-1">Hombre</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Teléfono con código de país */}
                    <div>
                      <Label className="text-gray-300 text-sm mb-1 flex items-center gap-2">
                        <FaPhone className="text-pink-400" /> Número de WhatsApp *
                      </Label>
                      <div className="flex gap-2">
                        <select
                          value={guestCountryCode}
                          onChange={(e) => setGuestCountryCode(e.target.value)}
                          className="bg-white/10 border border-white/20 rounded-lg px-2 py-2 text-white text-sm w-28"
                          style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                          data-testid="country-code-select"
                        >
                          {COUNTRY_CODES.map((cc) => (
                            <option key={cc.code} value={cc.code} className="bg-gray-800">
                              {cc.flag} {cc.code}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="tel"
                          value={guestPhone}
                          onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, ''))}
                          placeholder="3001234567"
                          className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                          data-testid="guest-phone-input"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Usaremos este número para contactarte</p>
                    </div>

                    {/* Cédula / DNI — opcional pero mejora aprobación en Mercado Pago */}
                    <div>
                      <Label className="text-white mb-2 flex items-center gap-2">
                        <FaIdCard className="text-pink-400" /> Cédula o Documento
                        <span className="text-xs text-gray-400 font-normal">(opcional — reduce rechazos)</span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={guestIdentification}
                        onChange={(e) => setGuestIdentification(e.target.value.replace(/\D/g, ''))}
                        placeholder="1024567890"
                        className="w-full bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        data-testid="guest-identification-input"
                        maxLength={15}
                      />
                      <p className="text-xs text-gray-500 mt-1">Ayuda a que Mercado Pago apruebe tu pago sin marcarlo como riesgoso</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Si está autenticado, mostrar mensaje */}
              {isAuthenticated && (
                <div className="p-6 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                  <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                    <FaCheckCircle className="text-green-400 text-xl" />
                    <div>
                      <p className="text-green-400 font-semibold">Sesión activa</p>
                      <p className="text-gray-400 text-sm">{guestEmail}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="p-6 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                <h2 className="text-xl font-bold text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Método de Pago
                </h2>
                <p className="text-sm text-gray-400">Selecciona cómo quieres pagar</p>
              </div>
              <div className="p-6 space-y-4">
                {!showPaymentProof ? (
                  <>
                    {/* Nota de pagos internacionales */}
                    <div className="text-center text-sm p-3 rounded-lg mb-4" style={{ backgroundColor: 'rgba(0, 200, 83, 0.1)', color: '#4ade80', border: '1px solid rgba(0, 200, 83, 0.3)' }}>
                      🌍 Se aceptan pagos con tarjeta a nivel mundial
                    </div>

                    {/* MercadoPago */}
                    <div 
                      className="p-4 border-2 rounded-xl cursor-pointer transition-all"
                      style={{ 
                        borderColor: selectedMethod === 'mercadopago' ? '#009ee3' : 'rgba(255,255,255,0.1)',
                        backgroundColor: selectedMethod === 'mercadopago' ? 'rgba(0, 158, 227, 0.15)' : 'rgba(255,255,255,0.05)'
                      }}
                      onClick={() => { setSelectedMethod('mercadopago'); setSelectedManualMethod(null); scrollToPayCta(); }}
                      data-testid="payment-method-mercadopago"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 bg-gradient-to-r from-[#009ee3] to-[#00b1ea] rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-md">
                          MP
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-lg text-white">Mercado Pago</div>
                          <div className="text-sm text-green-400">Pagos en pesos colombianos</div>
                          <div className="text-xs text-gray-400 mt-1">Tarjetas, PSE, efectivo, Nequi</div>
                        </div>
                      </div>
                    </div>

                    {/* ═══════ MÉTODOS MANUALES (Nequi / Bancolombia / Transferencia / PayPal) ═══════ */}
                    {manualMethodsConfig?.nequi?.enabled && (
                      <div
                        className="p-4 border-2 rounded-xl cursor-pointer transition-all"
                        style={{
                          borderColor: selectedManualMethod === 'nequi' ? '#a855f7' : 'rgba(255,255,255,0.1)',
                          backgroundColor: selectedManualMethod === 'nequi' ? 'rgba(168, 85, 247, 0.15)' : 'rgba(255,255,255,0.05)'
                        }}
                        onClick={() => { setSelectedMethod('nequi'); setSelectedManualMethod('nequi'); scrollToPayCta(); }}
                        data-testid="payment-method-nequi"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-fuchsia-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md">
                            Nq
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-lg text-white">Nequi</div>
                            <div className="text-xs text-purple-300 mt-1">Transferencia manual · Activación después de comprobante</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {manualMethodsConfig?.bancolombia?.enabled && (
                      <div
                        className="p-4 border-2 rounded-xl cursor-pointer transition-all"
                        style={{
                          borderColor: selectedManualMethod === 'bancolombia' ? '#facc15' : 'rgba(255,255,255,0.1)',
                          backgroundColor: selectedManualMethod === 'bancolombia' ? 'rgba(250, 204, 21, 0.15)' : 'rgba(255,255,255,0.05)'
                        }}
                        onClick={() => { setSelectedMethod('bancolombia'); setSelectedManualMethod('bancolombia'); scrollToPayCta(); }}
                        data-testid="payment-method-bancolombia"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md">
                            BC
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-lg text-white">Bancolombia</div>
                            <div className="text-xs text-yellow-200 mt-1">Transferencia manual · Activación después de comprobante</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {manualMethodsConfig?.transferencia?.enabled && (
                      <div
                        className="p-4 border-2 rounded-xl cursor-pointer transition-all"
                        style={{
                          borderColor: selectedManualMethod === 'transferencia' ? '#94a3b8' : 'rgba(255,255,255,0.1)',
                          backgroundColor: selectedManualMethod === 'transferencia' ? 'rgba(148, 163, 184, 0.15)' : 'rgba(255,255,255,0.05)'
                        }}
                        onClick={() => { setSelectedMethod('transferencia'); setSelectedManualMethod('transferencia'); scrollToPayCta(); }}
                        data-testid="payment-method-transferencia"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 bg-gradient-to-r from-slate-500 to-slate-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md">
                            TR
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-lg text-white">Transferencia (otros bancos)</div>
                            <div className="text-xs text-slate-300 mt-1">PSE / Davivienda / BBVA / etc.</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {manualMethodsConfig?.paypal?.enabled && (
                      <div
                        className="p-4 border-2 rounded-xl cursor-pointer transition-all"
                        style={{
                          borderColor: selectedManualMethod === 'paypal' ? '#003087' : 'rgba(255,255,255,0.1)',
                          backgroundColor: selectedManualMethod === 'paypal' ? 'rgba(0, 48, 135, 0.25)' : 'rgba(255,255,255,0.05)'
                        }}
                        onClick={() => { setSelectedMethod('paypal'); setSelectedManualMethod('paypal'); scrollToPayCta(); }}
                        data-testid="payment-method-paypal"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 bg-gradient-to-r from-[#003087] to-[#0079c1] rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md">
                            PP
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-lg text-white">PayPal</div>
                            <div className="text-xs text-blue-200 mt-1">Ideal para pagos internacionales</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ═══════ INSTRUCCIONES DEL MÉTODO MANUAL SELECCIONADO ═══════ */}
                    {selectedManualMethod && manualMethodsConfig?.[selectedManualMethod] && (
                      <div
                        className="p-5 rounded-2xl mt-3"
                        style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.10), rgba(0, 240, 255, 0.05))', border: '1px solid rgba(255,255,255,0.12)' }}
                        data-testid="manual-payment-instructions"
                      >
                        <p className="text-xs uppercase tracking-widest font-bold text-pink-300 mb-3">Datos para transferir</p>

                        {selectedManualMethod === 'nequi' && (
                          <div className="space-y-2 text-sm">
                            <p className="text-white"><span className="text-gray-400">Nequi:</span> <span className="font-bold text-white">{manualMethodsConfig.nequi.account_number || '—'}</span></p>
                            <p className="text-white"><span className="text-gray-400">A nombre de:</span> {manualMethodsConfig.nequi.account_holder || '—'}</p>
                          </div>
                        )}

                        {selectedManualMethod === 'bancolombia' && (
                          <div className="space-y-2 text-sm">
                            <p className="text-white"><span className="text-gray-400">Cuenta {manualMethodsConfig.bancolombia.account_type || 'Ahorros'}:</span> <span className="font-bold text-white">{manualMethodsConfig.bancolombia.account_number || '—'}</span></p>
                            <p className="text-white"><span className="text-gray-400">A nombre de:</span> {manualMethodsConfig.bancolombia.account_holder || '—'}</p>
                          </div>
                        )}

                        {selectedManualMethod === 'transferencia' && (
                          <div className="text-sm text-white/90 whitespace-pre-wrap">
                            {manualMethodsConfig.transferencia.instructions || 'Envía tu transferencia y luego comparte el comprobante por WhatsApp.'}
                          </div>
                        )}

                        {selectedManualMethod === 'paypal' && (
                          <div className="space-y-2 text-sm">
                            {manualMethodsConfig.paypal.email && (
                              <p className="text-white"><span className="text-gray-400">PayPal:</span> <span className="font-bold text-white">{manualMethodsConfig.paypal.email}</span></p>
                            )}
                            {manualMethodsConfig.paypal.paypal_link && (
                              <p className="text-white break-all"><span className="text-gray-400">Link:</span>{' '}
                                <a href={manualMethodsConfig.paypal.paypal_link} target="_blank" rel="noreferrer" className="text-cyan-300 underline">
                                  {manualMethodsConfig.paypal.paypal_link}
                                </a>
                              </p>
                            )}
                          </div>
                        )}

                        {manualMethodsConfig[selectedManualMethod].instructions && (
                          <div className="mt-3 text-xs text-white/70 italic whitespace-pre-wrap">
                            {manualMethodsConfig[selectedManualMethod].instructions}
                          </div>
                        )}

                        {/* Aviso de activación */}
                        {manualMethodsConfig.activation_note && (
                          <div className="mt-4 p-2 rounded-lg text-xs text-amber-200" style={{ background: 'rgba(251, 191, 36, 0.12)', border: '1px solid rgba(251,191,36,0.25)' }}>
                            ⏱️ {manualMethodsConfig.activation_note}
                          </div>
                        )}

                        {/* Botón WhatsApp para enviar comprobante */}
                        {(manualMethodsConfig.whatsapp_link || manualMethodsConfig.whatsapp_number) && (
                          <a
                            href={
                              manualMethodsConfig.whatsapp_link
                                ? manualMethodsConfig.whatsapp_link
                                : `https://wa.me/57${(manualMethodsConfig.whatsapp_number || '').replace(/\D/g, '')}?text=${encodeURIComponent(
                                    `¡Hola Deisy! Acabo de realizar un pago por ${plan?.name || 'un plan'}${plan?.price_display ? ' (' + plan.price_display + ')' : ''} vía ${selectedManualMethod.toUpperCase()}. Este es mi comprobante:`
                                  )}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            data-testid="manual-payment-whatsapp-link"
                            className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02]"
                            style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
                          >
                            💬 Enviar comprobante por WhatsApp
                          </a>
                        )}
                      </div>
                    )}

                    <Button 
                      className="w-full bg-gradient-to-r from-[#FF69B4] to-[var(--brand-primary-strong)] hover:opacity-90 text-white" 
                      size="lg" 
                      onClick={() => {
                        if (selectedManualMethod) {
                          // Método manual → registrar la compra como pendiente y mostrar pantalla de "ya pagué"
                          setShowPaymentProof(true);
                        } else {
                          handlePayment();
                        }
                      }}
                      disabled={!selectedMethod}
                      data-testid="confirm-payment-button"
                    >
                      {selectedManualMethod
                        ? 'Ya envié el pago — Confirmar'
                        : (!isAuthenticated ? 'Crear cuenta y Pagar' : 'Pagar con Mercado Pago')}
                    </Button>

                    <div className="text-center text-sm p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.1)' }}>
                      🔒 Pago 100% seguro - Serás redirigido a la pasarela de pago
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center py-6">
                      <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold mb-2 text-white">¡Casi listo!</h3>
                      <p className="text-gray-300 mb-4">
                        {selectedManualMethod
                          ? `Envíanos el comprobante por WhatsApp para activar tu ${plan?.name || 'plan'} lo antes posible.`
                          : '¿Ya realizaste el pago? Confirma para activar tu plan'}
                      </p>

                      {selectedManualMethod && (manualMethodsConfig?.whatsapp_link || manualMethodsConfig?.whatsapp_number) && (
                        <a
                          href={
                            manualMethodsConfig.whatsapp_link
                              ? manualMethodsConfig.whatsapp_link
                              : `https://wa.me/57${(manualMethodsConfig.whatsapp_number || '').replace(/\D/g, '')}?text=${encodeURIComponent(
                                  `¡Hola Deisy! Acabo de pagar por ${plan?.name || 'un plan'}${plan?.price_display ? ' (' + plan.price_display + ')' : ''} vía ${selectedManualMethod.toUpperCase()}. Este es mi comprobante:`
                                )}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          data-testid="proof-whatsapp-link"
                          className="mb-4 w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-white transition-all hover:scale-[1.02]"
                          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
                        >
                          💬 Enviar comprobante por WhatsApp
                        </a>
                      )}

                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200 mb-4">
                        <p className="text-sm font-semibold text-purple-800 mb-2">
                          📱 Si compraste un plan con acceso a WhatsApp (Premium o Gold)
                        </p>
                        <p className="text-xs text-gray-700">
                          Serás redirigido automáticamente para comenzar tu asesoría personalizada
                        </p>
                      </div>

                      <div className="flex gap-3">
                        <Button 
                          variant="outline"
                          onClick={() => setShowPaymentProof(false)}
                          className="flex-1"
                        >
                          Volver
                        </Button>
                        <Button 
                          onClick={handleSubmitProof}
                          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:opacity-90"
                          data-testid="confirm-payment-proof"
                        >
                          Ya Pagué - Activar Acceso
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;